/**
 * سربرگِ «کارنامه‌ی رجیستری» — مایگریشن 0085.
 *
 * یک سؤال، و فقط همین یکی: **کدام ردیفِ رجیستری ارزشش را دارد؟** جوابش نه
 * با «چند رویداد داد»، بلکه با چیزی که اگر آن منبع را حذف کنی از دست
 * می‌رود. شرحِ کامل، هشت اخطار و اعدادِ مبنا در
 * `plan/prompts/registry-scorecard.md`.
 *
 * ═══ چرا سربرگِ جدا و نه ستونی در «منابع» ═══
 *
 * «منابع» می‌گوید این سایت را می‌شناسیم و آخرین بار چه شد — یک ردیف در
 * رجیستری. این‌جا می‌گوید آن ردیف **چقدر می‌ارزد**، و درونش دو لایه
 * ریزبینی. سه ستونِ اضافه در آن جدول این را حل نمی‌کرد.
 *
 * ═══ ⚠ سه چیزی که بی‌صدا این صفحه را غلط می‌خوانند ═══
 *
 * ۱. **«انحصاری» با «ارزشمند» یکی نیست.** خوشه‌ی تک‌منبعی یا پوششِ
 *    انحصاریِ واقعی است یا شکستِ matcher. امروز روی داده‌ی واقعی ۲۳۰ از
 *    ۳۲۷ انحصارِ `613cal` مشکوک‌اند. تنها عددی که تصمیم می‌سازد
 *    `exclusive_publishable` است و ستونش این‌جا برجسته شده.
 *
 * ۲. **جمعِ ستون‌ها از کلِ رویدادها بیشتر است، و این درست است.** حکمِ
 *    publish روی خوشه است نه روی منبع، پس خوشه‌ی دومنبعی در ردیفِ هر دو
 *    شمرده می‌شود. `totals` جدا از دیتابیس می‌آید و **هرگز** از جمعِ
 *    ردیف‌های این جدول ساخته نمی‌شود.
 *
 * ۳. **جدولِ اصلی «وضعیت» است و جفت نمی‌شود، ولی جریانِ داخلِ هر ردیف
 *    جفت است.** از مایگریشنِ ۰۱۰۸ انتخابگرِ لود حذف شد و به‌جایش هر عددِ
 *    جریان دو تا شد: ۱۰ روزِ اخیر و آخرین اجرا. ستون‌های خودِ جدول
 *    (انحصار، سهمِ فیلدی، پیش‌دستی) از عضویتِ یک **نسل** درمی‌آیند و نسل
 *    یکجا ساخته می‌شود — جفت کردنشان یعنی یک عدد را دو بار نوشتن. نوارِ
 *    بالای جدول همین را می‌گوید و نباید برداشته شود.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { registryRepo, type RegistryRow } from '@/core/data/registryRepo';
import { sourceRepo } from '@/core/data/sourceRepo';
import { SourceCard } from './SourceCard';
import { fmtNum as fmt } from '@/ui/Num';
import { Hint } from '@/ui/Hint';

const stamp = (v: string | null) =>
  (v ? new Date(v).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' }) : '—');

/**
 * میانه‌ی «چقدر زودتر از بقیه پیدایش کرد».
 *
 * واحد از خودِ عدد می‌آید و ثابت نیست: با «ساعت»ِ ثابت، ۱۷۴ ثانیه «۰ ساعت»
 * نشان داده می‌شد — که شبیهِ «هیچ» است در حالی که یعنی «سه دقیقه».
 */
const lead = (sec: number | null) => {
  if (sec == null) return '—';
  const a = Math.abs(sec);
  if (a < 90) return `${Math.round(sec)} ثانیه`;
  if (a < 3600 * 2) return `${Math.round(sec / 60)} دقیقه`;
  if (a < 3600 * 48) return `${Math.round(sec / 3600)} ساعت`;
  return `${Math.round(sec / 86400)} روز`;
};

/**
 * هر نشانِ بهداشتی یک سؤالِ باز است، نه یک خطا.
 *
 * ⚠️ نامِ نشان انگلیسی و کوتاه است چون کلیدِ داده است، پس **همه‌ی** معنی در
 * همین متن می‌نشیند. هر کدام سه چیز دارد: یعنی چه، یک مثال، و قدمِ بعدی —
 * چون سؤالِ کسی که ماوس را نگه می‌دارد همیشه سومی است.
 */
const HYGIENE: Record<string, string> = {
  'draft-only':
    'هنوز پیش‌نویس است و به رجیستری ترفیع نگرفته.\n\n'
    + 'مثال: کشفِ خودکار این سایت را پیشنهاد داده ولی هنوز ردیفِ رسمی ندارد. '
    + 'اعدادِ این خط از کارِ واقعیِ کرالر می‌آیند، ولی پشتشان تنظیماتِ ثبت‌شده '
    + 'نیست.\n\n'
    + 'قدمِ بعدی: هیچ. زنجیره‌ی شبانه هر شب تا ۲۰ پیش‌نویس را خودش غربال و '
    + 'ترفیع می‌کند. اگر عجله داری، سربرگِ «منابع».',

  'enabled-zero-yield':
    'روشن است و کرالر هر شب می‌خواندش، ولی هیچ خوشه‌ای نساخته — یعنی هزینه '
    + 'می‌دهیم و چیزی نمی‌گیریم.\n\n'
    + 'مثال: صفحه سالم گرفته می‌شود (خطا هم ندارد) ولی خواننده‌اش هیچ رویدادی '
    + 'از تویش درنمی‌آورد.\n\n'
    + 'قدمِ بعدی: در سربرگِ «منابع» تستش کن. ستونِ «لازم دارد» می‌گوید مشکل یک '
    + 'تنظیم است یا کار کد.',

  'no-goout-source':
    'اسلاگِ اپ ندارد، پس نمی‌شود گفت کدام رویدادِ منتشرشده‌ی اپ از این منبع '
    + 'آمده.\n\n'
    + 'مثال: ستونِ «منتشرشده»ی این منبع صفر می‌ماند حتی وقتی واقعاً چیزی '
    + 'منتشر کرده. عدد گم نشده — فقط قابلِ نسبت دادن نیست.\n\n'
    + 'قدمِ بعدی: اسلاگ را در رجیستری تنظیم کن، وگرنه ارزشِ این منبع همیشه '
    + 'کمتر از واقع دیده می‌شود.',

  'never-inspected':
    'هیچ‌وقت غربال نشده — هیچ‌کس صفحه‌اش را با دو گیتِ «استخراج» و «رویداد» '
    + 'نسنجیده.\n\n'
    + 'مثال: ردیفی که مدت‌ها در فهرست نشسته ولی حکمِ غربالگری ندارد. تا وقتی '
    + 'حکمی نباشد، ترفیع این ردیف را **رد نمی‌کند** — فقط کنار می‌گذارد.\n\n'
    + 'قدمِ بعدی: دکمه‌ی «بررسی» در سربرگِ «منابع»، یا صبر تا زنجیره‌ی شبانه.',

  'all-rejected':
    'همه‌ی خوشه‌هایش حکمِ رد گرفته‌اند — کرالر می‌خواندش ولی هیچ‌کدام از '
    + 'نتیجه‌هایش به اپ نمی‌رود.\n\n'
    + 'مثال: سایتی که فقط رویدادهای گذشته دارد، یا لینک‌هایش همیشه به منبعِ '
    + 'دیگری می‌رود که ما آن را اصل می‌دانیم.\n\n'
    + 'قدمِ بعدی: چند نمونه‌اش را باز کن و ببین رد شدن درست بوده یا نه. اگر '
    + 'درست بوده، منبع را خاموش کن تا هزینه‌ی شبانه ندهد.',

  'exclusivity-suspect':
    'بیشترِ انحصارِ این منبع مشکوک است — یعنی احتمالاً اصلاً انحصاری نیست و '
    + 'فقط matcher نتوانسته به منبعِ دیگری وصلشان کند.\n\n'
    + 'مثال: ستونِ «انحصاری» ۴۰ نشان می‌دهد ولی ۳۰ تایش «مشکوک» است. تصمیم '
    + 'گرفتن از روی آن ۴۰ اشتباه است.\n\n'
    + 'قدمِ بعدی: جدولِ «هم‌پوشانی» پایینِ همین صفحه می‌گوید کدام منبع رقیبِ '
    + 'احتمالی‌اش است.',
};

/**
 * توضیحِ ستون‌های جدولِ کارنامه — سرِ ستون، جایی که سؤال پرسیده می‌شود.
 *
 * ⚠️ این شش ستون تصمیمِ «این منبع را نگه داریم یا نه» را می‌سازند و هیچ‌کدام
 * از نامشان معلوم نیستند: «انحصاری» و «انحصارِ منتشرشدنی» شبیهِ هم‌اند و
 * یکی‌شان تصمیم می‌سازد و آن یکی نه؛ «مشکوک» اصلاً نمی‌گوید به چه چیزی
 * مشکوک است. سرِ ستونِ جدولِ پهن هم جایی نیست که بشود جمله نوشت.
 */
const COL_HELP = {
  exclusive:
    'خوشه‌هایی که فقط این منبع گزارششان کرده — هیچ منبعِ دیگری همان رویداد را '
    + 'نیاورده.\n\n'
    + 'مثال: از ۲۰۰ رویدادی که این سایت داد، ۴۰ تا را هیچ‌جای دیگری نداشتیم. '
    + 'آن ۴۰ تا انحصارِ اوست؛ بقیه در ستونِ «مشترک» می‌نشینند.\n\n'
    + '⚠️ «انحصاری» با «ارزشمند» یکی نیست. ستونِ «مشکوک» کناری را حتماً با '
    + 'همین با هم بخوان.',

  suspect:
    'بخشی از همان «انحصاری» که احتمالاً واقعاً انحصاری نیست: کرالر یک شباهتِ '
    + 'نامطمئن به منبعِ دیگری دیده ولی نتوانسته قطعی بگوید یکی‌اند.\n\n'
    + 'مثال: این سایت نوشته «کنسرت جاز — سالن ملی» و سایتِ دیگری «شب جاز در '
    + 'سالن ملی». matcher نتوانست تصمیم بگیرد، پس دو خوشه‌ی جدا ماندند و این '
    + 'یکی «انحصاری» شمرده شد — در حالی که نیست.\n\n'
    + 'قدمِ بعدی: وقتی بیشترِ انحصارِ یک منبع مشکوک باشد ستون قرمز می‌شود، و '
    + 'آن یعنی ستونِ «انحصاری» برای تصمیم گرفتن قابلِ اتکا نیست.',

  publishable:
    'از آن انحصاری‌ها، آن‌هایی که واقعاً به دستِ کاربر می‌رسند — تاریخ و لینک '
    + 'دارند و حکمِ انتشار گرفته‌اند.\n\n'
    + 'مثال: ۴۰ تا انحصاری بود، ولی ۱۲ تایشان تاریخ نداشتند و منتشر نمی‌شوند. '
    + 'این ستون ۲۸ نشان می‌دهد.\n\n'
    + '⚠️ تنها عددی که تصمیم می‌سازد. «انحصاری» می‌گوید چه چیزی فقط این‌جاست؛ '
    + 'این یکی می‌گوید چه چیزی فقط این‌جاست **و به اپ می‌رسد**. اگر این منبع '
    + 'را برداری، همین عدد است که از دست می‌رود.',

  soleField:
    'رویدادهایی که خودشان از چند منبع آمده‌اند، ولی دستِ‌کم یک فیلدشان را فقط '
    + 'این منبع داشت.\n\n'
    + 'مثال: سه سایت همان کنسرت را دارند، ولی قیمتِ بلیط را فقط این یکی '
    + 'نوشته. آن رویداد این‌جا شمرده می‌شود.\n\n'
    + 'چرا مهم است: منبعی که هیچ رویدادِ انحصاری ندارد باز هم می‌تواند ارزش '
    + 'داشته باشد، چون دارد جاهای خالیِ بقیه را پر می‌کند.',

  fill:
    'به‌طورِ متوسط هر رویدادِ این منبع چند فیلدِ پر دارد. عددِ بزرگ‌تر یعنی '
    + 'کارتِ کامل‌تر برای کاربر.\n\n'
    + 'مثال: منبعی با پرکردگیِ ۸ عنوان و تاریخ و مکان و توضیح و قیمت می‌دهد؛ '
    + 'منبعی با ۳ فقط عنوان و تاریخ و لینک.\n\n'
    + 'قدمِ بعدی: پرکردگیِ پایین به‌تنهایی بد نیست. ولی پرکردگیِ پایین + '
    + 'انحصارِ کم یعنی این منبع فقط دارد هزینه می‌تراشد.',

  lead:
    'چند بار این منبع اولین جایی بوده که یک رویداد را دیدیم، و کنارش '
    + 'به‌طورِ متوسط چقدر زودتر از منبعِ بعدی.\n\n'
    + 'مثال: «۱۲ · ۲ روز» یعنی دوازده رویداد را اول این منبع آورد، به‌طورِ '
    + 'میانه دو روز زودتر از بقیه.\n\n'
    + 'چرا مهم است: منبعی که همان رویدادهای بقیه را می‌آورد ولی چند روز '
    + 'زودتر، ارزشش در سرعت است نه در انحصار — و از ستون‌های دیگر پیدا نیست.',
};

export function RegistryTab() {
  const [city, setCity] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [minShared, setMinShared] = useState(1);

  // شهرها فقط برای منطقه‌ی زمانی و فیلتر لازم‌اند.
  const cities = useQuery({ queryKey: ['pipelineCities'], queryFn: sourceRepo.cities });

  const card = useQuery({
    queryKey: ['regScorecard', city],
    queryFn: () => registryRepo.scorecard(city || null, 200),
  });
  const overlap = useQuery({
    queryKey: ['regOverlap', minShared],
    queryFn: () => registryRepo.overlap(null, minShared),
  });

  if (card.isLoading) return <p className="dim">…</p>;
  if (card.error) {
    return <div className="card"><p className="err mono">{String(card.error)}</p></div>;
  }
  // روی دیتابیسی بدونِ اسکیمای کرالر، تابع به‌جای ترکیدن `error` می‌دهد.
  if (card.data?.error) {
    return (
      <div className="card">
        <h2>در دسترس نیست</h2>
        <p className="dim">
          {card.data.error === 'not-precomputed'
            ? 'این گزارش هنوز ساخته نشده. کارنامه را کرالر می‌سازد؛ تا اجرای'
              + ' بعدی‌اش این صفحه چیزی برای نشان دادن ندارد.'
            : 'این دیتابیس اسکیمای crawler را ندارد — این صفحه فقط روی'
              + ' دیتابیسی معنی دارد که کرالر تویش می‌نویسد.'}
        </p>
      </div>
    );
  }

  // همیشه آخرین اجرای کامل — خودِ کارنامه شناسنامه‌اش را همراه می‌آورد.
  const chosen = card.data?.load;
  const rows = card.data?.rows ?? [];
  const tzOf = (c: string | null) =>
    (cities.data ?? []).find((x) => x.city === c)?.timezone ?? null;

  const shown = q.trim()
    ? rows.filter((r) => `${r.source_id} ${r.label ?? ''}`.toLowerCase()
        .includes(q.trim().toLowerCase()))
    : rows;

  return (
    <>
      <div className="topbar" style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <div className="filters">
          <label className="dim" htmlFor="rcity">شهر</label>
          <select id="rcity" value={city} onChange={(e) => { setCity(e.target.value); setOpen(null); }}>
            <option value="">همه</option>
            {(cities.data ?? []).map((c) => (
              <option key={c.city} value={c.city}>{c.label || c.city}</option>
            ))}
          </select>
          <input value={q} placeholder="نام یا شناسه" dir="ltr"
            onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {/* ⚠ فیلترِ شهر ردیف‌های پیش‌نویس را بیرون می‌اندازد و این عارضه است
          نه انتخاب: پیش‌نویسی که ترفیع نگرفته `city` ندارد. اگر گفته نشود،
          آدم فکر می‌کند آن منابع اصلاً وجود ندارند. */}
      {city ? (
        <p className="note">
          با فیلترِ شهر، ردیف‌های <span className="mono">draft-only</span> از جدول
          می‌افتند — پیش‌نویسی که ترفیع نگرفته در رجیستری نیست، پس شهر ندارد.
          برای دیدنشان فیلتر را بردار.
        </p>
      ) : null}

      {/* ══ همان لودی که انتخاب شده ══ */}
      {chosen ? (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="filters">
            <h2 style={{ margin: 0 }}>
              {stamp(chosen.started_at)} · <span className="mono">{chosen.command ?? '—'}</span>
            </h2>
            <span className="spacer" style={{ flex: 1 }} />
            {/* ⚠ کارنامه همیشه آخرین اجرای **کامل** را می‌گیرد (مایگریشنِ
                ۰۱۰۹). اگر هیچ اجرای کاملی نبوده، ردیفی که این‌جا نشسته
                تک‌فازی است و اعدادِ ستونِ اجرا را باید با همان چشم خواند. */}
            {chosen.full === false ? (
              <span className="chip warn">اجرای ناقص — هیچ اجرای کاملی ثبت نشده</span>
            ) : null}
            <span className={`chip${chosen.status === 'failed' ? ' warn' : ''}`}>
              {chosen.status ?? '—'}
            </span>
            <span className="chip">{chosen.seconds != null ? `${chosen.seconds}s` : '—'}</span>
          </div>

          {/* ⚠ نسخه‌ی مرحله‌ای که در این لود **نچرخیده** اصلاً نمی‌آید، نه
              اینکه نسخه‌ی قبلی را بگیرد. پر کردنش یعنی لودی که فقط policy
              دوانده شبیهِ لودی شود که کلِ زنجیره را دوانده. */}
          <p className="chiprow" style={{ marginTop: 8 }}>
            {Object.entries(chosen.versions ?? {}).length === 0 ? (
              <span className="dim">هیچ مرحله‌ای در این لود ردِ نسخه‌داری نگذاشته.</span>
            ) : Object.entries(chosen.versions ?? {}).map(([k, v]) => (
              <span key={k} className="chip mono">
                {k} {Array.isArray(v) ? v.join('/') : String(v)}
              </span>
            ))}
          </p>

          {/* این نوار جانِ صفحه است — نگاه کن به سرِ فایل، بندِ ۳. */}
          <p className="note">
            ستون‌های جدولِ زیر <b>وضعیت پس از همین اجرا</b>ست و یک عدد دارند،
            نه دو تا: انحصار و سهمِ فیلدی از عضویتِ یک نسل درمی‌آیند و نسل
            یکجا ساخته می‌شود، پس «انحصارِ ۱۰ روزِ اخیر» یا همان عددِ الان
            می‌شد یا صفر. اعدادِ <b>جریان</b> — درخواست، ادعا، mention،
            منتشرشده — داخلِ کارتِ هر منبع‌اند و آن‌ها جفت‌اند: ۱۰ روزِ اخیر و
            همین اجرا.
          </p>
        </div>
      ) : null}

      {/* ══ جدولِ اصلی ══ */}
      <div className="card">
        <div className="filters">
          <h2 style={{ margin: 0 }}>کارنامه</h2>
          <span className="spacer" style={{ flex: 1 }} />
          {card.data?.totals ? (
            // ⚠ از دیتابیس می‌آید و نه از جمعِ ردیف‌ها — سرِ فایل، بندِ ۲.
            <span className="dim">
              {fmt(card.data.totals.registries)} رجیستری ·
              {' '}{fmt(card.data.totals.clusters)} خوشه‌ی یکتا
            </span>
          ) : null}
        </div>

        {shown.length ? (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>منبع</th><th>شهر</th>
                  <th className="num">خوشه</th><th className="num">مشترک</th>
                  <th className="num"><Hint text="انحصاری" help={COL_HELP.exclusive} /></th>
                  <th className="num"><Hint text="مشکوک" help={COL_HELP.suspect} /></th>
                  <th className="num">
                    <Hint text="انحصارِ منتشرشدنی" help={COL_HELP.publishable} />
                  </th>
                  <th className="num">
                    <Hint text="رویدادِ فیلدِ انحصاری" help={COL_HELP.soleField} />
                  </th>
                  <th className="num"><Hint text="پرکردگی" help={COL_HELP.fill} /></th>
                  <th className="num"><Hint text="پیش‌دستی" help={COL_HELP.lead} /></th>
                  <th>نشان</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r: RegistryRow) => {
                  const st = r.state;
                  const suspectHeavy = st.exclusive > 0
                    && st.exclusive_suspect / st.exclusive > 0.5;
                  return (
                    <React.Fragment key={r.source_id}>
                      <tr>
                        <td>
                          <button type="button" className="linkbtn"
                            onClick={() => setOpen(open === r.source_id ? null : r.source_id)}>
                            <span className="mono">{r.source_id}</span>
                          </button>
                          {!r.enabled ? <span className="dim"> · خاموش</span> : null}
                        </td>
                        <td className={r.city ? '' : 'dim'}>{r.city ?? '—'}</td>
                        <td className="num">{fmt(st.clusters)}</td>
                        <td className="num">{fmt(st.shared)}</td>
                        <td className="num">{fmt(st.exclusive)}</td>
                        {/* ⚠ مشکوک یعنی «یک uncertain به منبعِ دیگری دارد» —
                            یعنی احتمالاً اصلاً انحصاری نیست. وقتی بیشترِ
                            انحصار مشکوک باشد، ستونِ کناری قابلِ تصمیم نیست. */}
                        <td className={`num${suspectHeavy ? ' bad' : ''}`}>
                          {fmt(st.exclusive_suspect)}
                        </td>
                        {/* تنها عددی که تصمیم می‌سازد. */}
                        <td className="num"><b>{fmt(st.exclusive_publishable)}</b></td>
                        <td className="num">{fmt(st.sole_field_events)}</td>
                        <td className="num">{st.avg_fields_filled ?? '—'}</td>
                        <td className="num">
                          {fmt(st.first_to_find)}
                          {st.median_lead_seconds != null ? (
                            <span className="dim"> · {lead(st.median_lead_seconds)}</span>
                          ) : null}
                        </td>
                        <td className="chiprow">
                          {r.hygiene.map((h) => (
                            <span key={h} className="chip warn" title={HYGIENE[h] ?? h}>{h}</span>
                          ))}
                        </td>
                      </tr>
                      {open === r.source_id ? (
                        <tr>
                          <td colSpan={11} style={{ padding: 0 }}>
                            <SourceCard row={r} tz={tzOf(r.city)} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : card.isLoading ? null : <p className="dim">ردیفی نیست.</p>}

        <p className="note">
          روی شناسه‌ی هر منبع بزن تا جریانِ همان لود، لینک‌ها، جدولِ فیلدها و
          نمونه‌های واقعی‌اش باز شود. «مشکوک» یعنی آن انحصار یک
          {' '}<span className="mono">uncertain</span> به منبعِ دیگری دارد — یعنی
          احتمالاً اصلاً انحصاری نیست و فقط matcher وصلشان نکرده.
        </p>
      </div>

      {/* ══ هم‌پوشانی ══ */}
      <div className="card">
        <div className="filters">
          <h2 style={{ margin: 0 }}>هم‌پوشانی</h2>
          <span className="spacer" style={{ flex: 1 }} />
          <label className="dim" htmlFor="minsh">حداقلِ اشتراک</label>
          <select id="minsh" value={minShared}
            onChange={(e) => setMinShared(Number(e.target.value))}>
            <option value={1}>۱</option>
            <option value={5}>۵</option>
            <option value={20}>۲۰</option>
          </select>
        </div>
        <p className="dim" style={{ marginBottom: 6 }}>
          «اگر این منبع را برداریم، کدام یکی پوششش را دارد؟»
        </p>
        {overlap.isLoading ? <p className="dim">…</p> : null}
        {overlap.data?.pairs?.length ? (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>منبعِ الف</th><th>منبعِ ب</th>
                  <th className="num">مشترک</th><th className="num">الف</th>
                  <th className="num">ب</th><th className="num">Jaccard</th>
                </tr>
              </thead>
              <tbody>
                {overlap.data.pairs.map((p) => (
                  <tr key={`${p.source_a}:${p.source_b}`}>
                    <td className="mono">{p.source_a}</td>
                    <td className="mono">{p.source_b}</td>
                    <td className="num">{fmt(p.shared)}</td>
                    <td className="num dim">{fmt(p.events_a)}</td>
                    <td className="num dim">{fmt(p.events_b)}</td>
                    <td className="num">{p.jaccard}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : overlap.isLoading ? null : (
          <p className="dim">هیچ جفتی خوشه‌ی مشترک ندارد.</p>
        )}
        {/* ⚠ خالی یا کم بودنِ این جدول **یافته است، نه نبودِ داده.** */}
        <p className="note">
          کم بودنِ این جدول خودش یک یافته است، نه نبودِ داده: یا منابع واقعاً
          پوششِ جدا دارند، یا matcher وصلشان نمی‌کند. ستونِ «مشکوک» در جدولِ
          بالا می‌گوید کدام.
        </p>
      </div>
    </>
  );
}
